/** QuickHouse — Visite virtuelle FPS : ZQSD/WASD + flèches pour se déplacer,
 *  souris pour tourner la tête (verrouillage du pointeur au clic), Maj = courir.
 *  Hauteur d'yeux 165 cm au niveau de l'étage actif. Pendant la visite, un clic
 *  (pointeur capturé) mesure le point visé au centre de l'écran si 📏 est actif,
 *  et le HUD (viseur, hauteur visée, cap, minimap) est alimenté ~10 fois/s. */
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { useProjectStore } from '../store/useProjectStore.js';

const EYE = 1.65; // m
const CENTER = new THREE.Vector2(0, 0); // centre de l'écran (NDC)

export default function WalkControls({ startElevation = 0 }) {
  const { camera, gl, scene } = useThree();
  const keys = useRef({});
  const yawPitch = useRef({ yaw: 0, pitch: 0 });
  const raycaster = useRef(new THREE.Raycaster());
  const hudClock = useRef(0);

  // Point visé au centre de l'écran (ignore les surfaces marquées noRay : billes de mesure…)
  const aimAtCenter = () => {
    raycaster.current.setFromCamera(CENTER, camera);
    const hits = raycaster.current.intersectObjects(scene.children, true);
    return hits.find((h) => !h.object.userData.noRay && h.object.visible) ?? null;
  };

  useEffect(() => {
    camera.rotation.order = 'YXZ';
    // Position de départ : au niveau demandé, orientation actuelle conservée en yaw
    yawPitch.current.yaw = camera.rotation.y;
    yawPitch.current.pitch = 0;
    camera.position.y = startElevation + EYE;
    useProjectStore.getState().setStatus(
      'Visite : cliquez sur la vue pour capturer la souris — ZQSD/WASD + flèches, Maj = courir, Échap libère la souris',
    );

    const dom = gl.domElement;
    const onKey = (down) => (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      keys.current[e.code] = down;
      if (down && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) e.preventDefault();
    };
    const onMouseMove = (e) => {
      if (document.pointerLockElement !== dom) return;
      yawPitch.current.yaw -= e.movementX * 0.0024;
      yawPitch.current.pitch = Math.max(-1.45, Math.min(1.45, yawPitch.current.pitch - e.movementY * 0.0024));
    };
    const onClick = () => {
      if (document.pointerLockElement !== dom) { dom.requestPointerLock(); return; }
      // Pointeur capturé : le clic mesure le point visé (au viseur central)
      const st = useProjectStore.getState();
      if (!st.measure3d) return;
      const hit = aimAtCenter();
      if (hit) st.addMeasurePoint({ x: hit.point.x, y: hit.point.y, z: hit.point.z });
    };
    const kd = onKey(true);
    const ku = onKey(false);
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    document.addEventListener('mousemove', onMouseMove);
    dom.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('keydown', kd);
      window.removeEventListener('keyup', ku);
      document.removeEventListener('mousemove', onMouseMove);
      dom.removeEventListener('click', onClick);
      if (document.pointerLockElement === dom) document.exitPointerLock();
      useProjectStore.getState().setStatus('');
      useProjectStore.getState().setWalkHud(null);
    };
  }, [camera, gl, startElevation]);

  useFrame((_, delta) => {
    const k = keys.current;
    camera.rotation.y = yawPitch.current.yaw;
    camera.rotation.x = yawPitch.current.pitch;
    const speed = (k.ShiftLeft || k.ShiftRight ? 5 : 2.4) * Math.min(delta, 0.1);
    const forward = (k.KeyW || k.KeyZ || k.ArrowUp ? 1 : 0) - (k.KeyS || k.ArrowDown ? 1 : 0);
    const strafe = (k.KeyD || k.ArrowRight ? 1 : 0) - (k.KeyA || k.KeyQ || k.ArrowLeft ? 1 : 0);
    if (forward || strafe) {
      const dir = new THREE.Vector3(Math.sin(camera.rotation.y), 0, Math.cos(camera.rotation.y));
      // Vecteur « droite » du regard : avant (−dir) tourné de −90° autour de Y
      const right = new THREE.Vector3(dir.z, 0, -dir.x);
      camera.position.addScaledVector(dir, -forward * speed);
      camera.position.addScaledVector(right, strafe * speed);
      camera.position.y = startElevation + EYE; // hauteur d'yeux constante
    }

    // HUD ~10 Hz : point visé (hauteur, distance), cap boussole, position sur le plan
    hudClock.current += delta;
    if (hudClock.current < 0.1) return;
    hudClock.current = 0;
    const st = useProjectStore.getState();
    const north = st.project?.settings.north ?? 0;
    const hit = aimAtCenter();
    // Azimut du regard dans le plan (depuis −Z, sens horaire) puis cap géographique
    const azPlan = ((-THREE.MathUtils.radToDeg(camera.rotation.y)) % 360 + 360) % 360;
    const heading = ((azPlan - north) % 360 + 360) % 360;
    st.setWalkHud({
      aim: hit ? { h: Math.round(hit.point.y * 100), d: hit.distance } : null,
      heading,
      azPlan,
      pos: { x: camera.position.x * 100, y: camera.position.z * 100 }, // cm, repère du plan 2D
    });
  });

  return null;
}
